<template>
  <BModal
    id="modalMap"
    ref="modalMap"
    no-close-on-esc
    no-close-on-backdrop
    hide-header-close
    hide-footer
    hide-header
    size="lg"
    :centered="true"
    @shown="initMap()"
  >
    <div class="text-center my-1 d-flex flex-column gap-4">
      <div class="font-bold text-xl text-black">
        Tambah Lokasi
      </div>
      <div
        class="position-relative w-100"
        style="height: 400px;"
      >
        <gmap-map
          ref="mapRef"
          style="width: 100%; height: 100%;"
          :zoom="zoom"
          :center="coordinate || coordinateTemp"
          @idle="handleIdle"
          @center_changed="updateCenter"
        />
        <span
          class="k-gps position-absolute cursor-pointer d-flex align-items-center justify-center text-3xl bg-white rounded-pill"
          style="bottom: 150px; right: 10px; width: 40px; height: 40px; z-index: 10;"
          @click="useCurrentLocation"
        />
      </div>
      <div class="d-flex">
        <div class="d-flex flex-column col-md-8 text-left">
          <strong class="text-base text-black">{{ addressMaps.name }}</strong>
          <span class="text-xs text-black">{{ addressMaps.address }}</span>
        </div>
        <div class="d-flex rounded-lg align-items-center border w-100 bg-white">
          <span
            class="k-search-normal-1 font-bold text-xl align-self-center mx-[5px]"
          />
          <gmap-autocomplete
            class="form-control"
            @place_changed="setPlace"
          />
        </div>
      </div>
      <div
        v-if="isZipCodeNotSame"
        class="w-100 bg-[#E31A1A] p-1 text-white font-medium my-2 text-left"
      >
        Titik lokasi berada di luar area kabupaten atau kota gudang
      </div>
      <div class="d-flex gap-3 justify-center col-md-4 align-self-end">
        <b-button
          variant="outline-primary"
          class="w-100"
          @click="$bvModal.hide('modalMap')"
        >
          Batal
        </b-button>
        <b-button
          variant="primary"
          class="w-100"
          :disabled="isZipCodeNotSame"
          @click="selectLocation()"
        >
          Submit
        </b-button>
      </div>
    </div>
  </BModal>
</template>

<script>
export default {
  props: {
    initialPlaces: {
      type: Object,
      default: () => {},
    },
    addressSelected: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      coordinateTemp: { lat: null, lng: null },
      coordinate: null,
      zoom: 12,
      addressMaps: {
        name: '',
        address: '',
        district: '',
        regency: '',
      },
    }
  },
  computed: {
    isZipCodeNotSame() {
      const { regency } = this.addressMaps
      const storeRegency = this.cleanLocationName(this.addressSelected?.city_name)
      const mapRegency = this.cleanLocationName(regency)

      return storeRegency !== mapRegency
    },
  },
  methods: {
    cleanLocationName(name) {
      if (!name) return ''
      let cleaned = name.toLowerCase()
        .replace(/^(kab\.|kabupaten|kota|regency|city of)\s+/g, '')
        .replace(/\s+(regency|city|district)$/g, '')
        .trim()

      const translations = {
        south: 'selatan',
        north: 'utara',
        west: 'barat',
        east: 'timur',
        central: 'pusat',
      }

      const directions = Object.keys(translations).join('|')
      const regex = new RegExp(`^(${directions})\\s+(.+)$`, 'i')
      const match = cleaned.match(regex)

      if (match) {
        const direction = match[1].toLowerCase()
        const cityName = match[2]
        cleaned = `${cityName} ${translations[direction]}`
      }

      return cleaned.trim()
    },
    updateCenter(latLng) {
      this.coordinateTemp = {
        lat: latLng.lat(),
        lng: latLng.lng(),
      }
    },
    handleIdle() {
      this.coordinate = { ...this.coordinateTemp }
      // If we just moved map manually, we need to fetch address for new center
      // Check if we already have the address info for this exact coordinate (optimization)
      // but for simplicity, just fetch. Use debounce if needed in future.
      this.fetchAddress(this.coordinate)
    },
    /**
     * Unified method to parse address components from Google Maps API result
     * @param {Object} result - PlaceResult or GeocoderResult
     * @param {Boolean} isAutocomplete - true if from autocomplete (has name)
     */
    parseAddressResult(result, isAutocomplete = false) {
      const { formatted_address, address_components, name } = result
      const addressParts = formatted_address?.split(',')

      // Logic from original code:
      // If parts > 2, slice(1, -1), else use full.
      this.addressMaps.address = addressParts?.length > 2
        ? addressParts.slice(1, -1).join(',').trim()
        : formatted_address

      // Logic for name:
      if (isAutocomplete) {
        this.addressMaps.name = name
      } else {
        // From geocoder
        this.addressMaps.name = addressParts?.length > 0
          ? addressParts[0]
          : this.addressMaps.address
      }

      const getComponent = type => address_components.find(c => c.types.includes(type))?.long_name || ''
      this.addressMaps.district = getComponent('administrative_area_level_3')
      this.addressMaps.regency = getComponent('administrative_area_level_2')
    },

    setPlace(place) {
      if (!place.geometry) return
      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()

      // Update map center
      this.setMapCenter({ lat, lng })

      // Parse address immediately since we have the place details
      this.parseAddressResult(place, true)
    },

    selectLocation() {
      this.$emit('selected', {
        ...this.coordinate,
        address: this.addressMaps.address,
      })
      this.$refs.modalMap.hide()
    },

    setMapCenter({ lat, lng }) {
      this.coordinateTemp = { lat, lng }
      this.coordinate = { lat, lng }
      this.zoom = 16

      // Force map pan
      this.$refs.mapRef.$mapPromise.then(map => {
        // eslint-disable-next-line
        const google = window.google || google
        google.maps.event.trigger(map, 'resize')
        map.setCenter({ lat, lng })
      })
    },

    fetchAddress({ lat, lng }) {
      if (!lat || !lng) return
      this.$geocoder.send({ lat, lng }, response => {
        if (response.results?.length) {
          this.parseAddressResult(response.results[0], false)
        }
      })
    },

    useCurrentLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const { latitude, longitude } = position.coords
            this.setMapCenter({ lat: latitude, lng: longitude })
            this.fetchAddress({ lat: latitude, lng: longitude })
          },
          error => {
            console.error('Error getting location:', error)
          },
        )
      } else {
        console.error('Geolocation is not supported by this browser.')
      }
    },

    async initMap() {
      // 1. Initial coordinates provided
      if (this.initialPlaces?.lat) {
        this.setMapCenter({
          lat: parseFloat(this.initialPlaces.lat),
          lng: parseFloat(this.initialPlaces.lng),
        })
        // Fetch specific address for these coords
        this.fetchAddress({
          lat: parseFloat(this.initialPlaces.lat),
          lng: parseFloat(this.initialPlaces.lng),
        })
        return
      }

      // 2. Initial Query Label provided -> Search
      if (this.addressSelected?.label) {
        try {
          const map = await this.$refs.mapRef.$mapPromise
          // eslint-disable-next-line
          const google = window.google || google
          if (!google?.maps?.places) throw new Error('Maps API not loaded')

          const service = new google.maps.places.PlacesService(map)

          service.findPlaceFromQuery({
            query: this.addressSelected.label,
            fields: ['place_id', 'geometry'],
          }, (results, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && results?.[0]) {
              service.getDetails({
                placeId: results[0].place_id,
                fields: ['name', 'geometry', 'formatted_address', 'address_components'],
              }, (place, detailStatus) => {
                if (detailStatus === google.maps.places.PlacesServiceStatus.OK) {
                  this.setPlace(place)
                } else {
                  this.fetchAddress(this.coordinateTemp)
                }
              })
            } else {
              this.fetchAddress(this.coordinateTemp)
            }
          })
        } catch (e) {
          console.error(e)
          this.fetchAddress(this.coordinateTemp)
        }
      } else {
        // 3. Fallback to default (usually current location or 0,0 if initialized that way)
        this.fetchAddress(this.coordinateTemp)
      }
    },
  },
}
</script>
